import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { isEmpty } from "lodash";
import moment from "moment";
// FetchAPI, not the axios APIClient: APIClient carries no login token and
// sends Access-Control-Allow-Origin / Cache-Control request headers the API's
// CORS allow-list rejects, so the browser blocked the call ("Network Error").
import { FetchAPI } from "../../helpers/APIdata/fetchAPI";

const fmtDuration = (fromIso, now) => {
  const mins = Math.max(0, Math.floor((now - new Date(fromIso.replace(" ", "T"))) / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
};

/**
 * Change #100332 - clock in / out for the shift from the web, inside the
 * top-bar timer. Some staff only ever use the web portal and had no way to
 * clock in short of the wall tablet.
 *
 * Uses the same staffclock/status | clockin | clockout endpoints as the kiosk;
 * with a personal login they act on the signed-in user's own record (Source
 * "web"), and lateness against the rota is worked out server-side as usual.
 *
 * When status does not come back 200 (e.g. no clock permission, AUTHCAP2350X)
 * it shows one muted line saying why, rather than vanishing.
 *
 * Clock out is held back while a timer is running: the server would close the
 * open time entry itself, skipping the timer's own stop flow (job-complete
 * prompt, billing checks) and leaving the timer on screen out of step.
 */
export default function ShiftClockPanel({ open, timerRunning, onHideTimerChange }) {
  const [state, setState] = useState(null); // staffclock_state, or null = hidden

  // Change #100695 - tell the timer whether this staff member has the job
  // timer switched off (Staff.FlagHideTimer). Unknown / unavailable = shown.
  useEffect(() => {
    onHideTimerChange?.(Number(state?.FlagHideTimer) === 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.FlagHideTimer]);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());

  // Why the strip cannot work, shown instead of silently hiding it - a strip
  // that just is not there was impossible to tell apart from "not deployed".
  const [unavailable, setUnavailable] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await FetchAPI("/staffclock/status", "POST", { Source: "web" });
      if (res?.status === 200 && res.staffclock_state) {
        setState(res.staffclock_state);
        setUnavailable("");
      } else {
        setState(null);
        setUnavailable(res?.message || `status ${res?.status ?? "unknown"}`);
      }
    } catch (e) {
      setState(null);
      // The axios interceptor rejects with a plain message string.
      setUnavailable(typeof e === "string" ? e : e?.message || "request failed");
    }
  }, []);

  // Refresh each time the timer dropdown opens: a kiosk or the app may have
  // clocked them in or out since.
  useEffect(() => {
    if (open) load();
  }, [open, timerRunning, load]);

  // Change #100335 - "when you stop your timer the clock in/out part doesn't
  // refresh". The timer flips timerIsRunning(false) BEFORE its stop request
  // has reached the server, so the reload above ran while the time entry was
  // still open and "stop your timer first" stuck until F5. Reload again when
  // the server's stop / start reply actually lands.
  const stopReply = useSelector((s) => s.TimeEntries?.timeEntryStopSuccess);
  const startReply = useSelector(
    (s) => s.TimeEntries?.timeEntryData?.timeentry_data?.timeEntryStartSuccess
  );
  useEffect(() => {
    if (open && !isEmpty(stopReply)) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopReply]);
  useEffect(() => {
    if (open && !isEmpty(startReply)) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startReply]);

  // Tick the "on shift for" figure while it is visible.
  useEffect(() => {
    if (!open || !state?.FlagClockedIn) return undefined;
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, [open, state?.FlagClockedIn]);

  const act = async (endpoint, okFallback) => {
    setBusy(true);
    try {
      const res = await FetchAPI(`/staffclock/${endpoint}`, "POST", { Source: "web" });
      if (res?.status === 200) {
        toast.success(res.message || okFallback);
        setState(res.staffclock_state || null);
        setNow(Date.now());
      } else {
        toast.error(res?.message || "Something went wrong");
        load();
      }
    } catch (e) {
      toast.error(e?.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  if (!state) {
    if (!unavailable) return null; // still loading
    return (
      <div className="shift-clock-panel border-bottom px-3 py-2 text-muted fs-12">
        <i className="ri-time-line align-middle me-1" />
        Clock in is not available: {unavailable}
      </div>
    );
  }

  const record = state.OpenClockRecord;
  const rota = state.TodayStaffShift;
  const clockedIn = Number(state.FlagClockedIn) === 1;
  const openEntry = !!state.OpenTimeEntry || timerRunning;

  return (
    <div className="shift-clock-panel border-bottom px-3 py-2">
      <div className="d-flex align-items-center gap-2">
        <span
          className={`shift-clock-panel__dot ${clockedIn ? "is-on" : ""}`}
          aria-hidden="true"
        />
        <div className="flex-grow-1 min-w-0">
          <div className="fw-semibold fs-13">
            {clockedIn ? "On shift" : "Not clocked in"}
            {clockedIn && Number(record?.StaffClockRecord_FlagLate) === 1 && (
              <span className="badge bg-warning-subtle text-warning ms-2">
                Late {record?.StaffClockRecord_LateMinutes}m
              </span>
            )}
          </div>
          <div className="text-muted fs-12 text-truncate">
            {clockedIn && record?.StaffClockRecord_ClockInDateTime
              ? `Since ${moment(record.StaffClockRecord_ClockInDateTime).format("HH:mm")} · ${fmtDuration(
                  record.StaffClockRecord_ClockInDateTime,
                  now
                )}`
              : rota?.StaffShift_ShiftStartDateTime
              ? `Today's shift ${moment(rota.StaffShift_ShiftStartDateTime).format("HH:mm")}${
                  rota.StaffShift_ShiftEndDateTime
                    ? `–${moment(rota.StaffShift_ShiftEndDateTime).format("HH:mm")}`
                    : ""
                }`
              : "Clock in to start your shift"}
          </div>
        </div>
        {clockedIn ? (
          <button
            type="button"
            className="btn btn-sm btn-soft-danger"
            disabled={busy || openEntry}
            title={openEntry ? "Stop your timer first" : "End your shift"}
            onClick={() => act("clockout", "Clocked out")}
          >
            <i className="ri-logout-box-r-line align-bottom me-1" />
            Clock out
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-sm btn-success"
            disabled={busy}
            onClick={() => act("clockin", "Clocked in")}
          >
            <i className="ri-login-box-line align-bottom me-1" />
            Clock in
          </button>
        )}
      </div>
      {/* Change #100367 - a forgotten clock-in from an earlier shift; clocking in closes it */}
      {!clockedIn && Number(state.FlagStaleClockIn) === 1 && state.StaleClockRecord?.StaffClockRecord_ClockInDateTime && (
        <div className="text-warning fs-11 mt-1">
          You didn't clock out after clocking in{" "}
          {moment(state.StaleClockRecord.StaffClockRecord_ClockInDateTime).format("ddd D MMM HH:mm")}. Clock in now and
          your manager will add that time.
        </div>
      )}
      {clockedIn && openEntry && (
        <div className="text-muted fs-11 mt-1">
          Stop your timer first, then clock out.
        </div>
      )}
    </div>
  );
}
